// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.
//
// ─────────────────────────────────────────────────────────────────────────
// PROVISIONAL CONTRACT SHAPES — replace with #13 output when it lands.
//
// Issue #16 states: "UI scaffolding + contract-shaped mocks can start once #13
// lands." #13 (data-models & contracts) is not yet on `main`, so the shapes
// below are this widget's OWN contract-shaped stand-ins, derived directly from
// the Accepted ADRs (ADR-003 identity, ADR-004 D4 two-layer display) and the
// committed bridge form. They are isolated here so that when #13 lands, only
// this file and ../contracts/* change — no feature/render code should encode
// field shapes directly.
// ─────────────────────────────────────────────────────────────────────────

/**
 * A field that may be unknown. ADR-004 D4 / README: a field a producer does not
 * supply renders the literal "unknown" — never hidden, never fabricated. We
 * model that as an explicit union rather than `null`, so render code must make
 * a deliberate choice and cannot accidentally print an empty string.
 */
export type Unknownable<T> = T | { readonly unknown: true };

export const UNKNOWN: { readonly unknown: true } = { unknown: true };

export function isUnknown<T>(v: Unknownable<T>): v is { readonly unknown: true } {
  return typeof v === "object" && v !== null && "unknown" in v;
}

/** A raw transport value read as a displayable string, or UNKNOWN if blank/absent. */
export function toUnknownableString(value: unknown): Unknownable<string> {
  if (typeof value === "string" && value.trim().length > 0) return value;
  return UNKNOWN;
}

/** A raw transport value read as a displayable boolean, or UNKNOWN if not a boolean. */
export function toUnknownableBoolean(value: unknown): Unknownable<boolean> {
  if (typeof value === "boolean") return value;
  return UNKNOWN;
}

/** Device identity layer — always shown (ADR-004 D4; README "Device identity information"). */
export interface DeviceIdentity {
  readonly serialNo: Unknownable<string>;
  readonly name: Unknownable<string>;
  readonly softwareId: Unknownable<string>;
  readonly hardwareId: Unknownable<string>;
  /**
   * Derived by the server from WHICH registry resolved the device — app74
   * (demo) vs app21 (production) — never selected client-side (ADR-008 D1).
   * Surfaced to the user as "demo" (ADR-008 D1/D3 "What this constrains");
   * also widget-carried forward into the FirmwareDelivery requests so the
   * seam can route on it (ADR-008 D2).
   */
  readonly isDemo: Unknownable<boolean>;
}

/**
 * Production / meta layer (ADR-004 D4, from FirmwareDeliveryAPIv1 by reference
 * id). `deploymentChannel` is the producing/updater AU's full name (D4).
 * `method` drives the D6 offline-augment trigger.
 */
export interface ProductionMetaLayer {
  readonly release: Unknownable<string>;
  readonly draftSolution: Unknownable<string>;
  readonly operationTime: Unknownable<string>;
  readonly operatedBy: Unknownable<string>;
  readonly method: Unknownable<string>;
  readonly deploymentChannel: Unknownable<string>;
}

/**
 * Updater / execution layer (ADR-004 D4, from the updater AU record).
 *
 * `executionStatus` carries the updater AU's raw `upload_status` — for
 * serial/BLE producers this progresses Triggered → Flashed → Executed (the
 * update was tetiklendi, flash'a yazıldı, sonra çalıştığı doğrulandı), but
 * the field is display heuristic over free text (see
 * `executionStatusPresentation` in device-view.tsx), not a closed enum: an
 * unrecognized value still renders in full, in a neutral tone.
 *
 * `writeStarted` is the raw `write_started` flag — whether the write/flash
 * step was itself begun, independent of how far `executionStatus` got.
 *
 * `faultOrigin` is the raw `fault_origin` — set only when the write/upload
 * was interrupted, attributing the interruption to `AU` (this AU's own
 * internal fault), `device` (the device's own internal system), `user`, or
 * `systemic` (broader system failure); UNKNOWN when nothing was recorded,
 * i.e. no fault occurred.
 */
export interface UpdaterExecutionLayer {
  readonly executionStatus: Unknownable<string>;
  readonly executionTime: Unknownable<string>;
  readonly executedBy: Unknownable<string>;
  readonly writeStarted: Unknownable<boolean>;
  readonly faultOrigin: Unknownable<string>;
}

/** One selected last operation = production/meta + updater/execution (ADR-004 D3/D4). */
export interface SelectedOperation {
  readonly meta: ProductionMetaLayer;
  readonly execution: UpdaterExecutionLayer;
}

/**
 * The AU0001-sourced winner's own display fields (ADR-004 D4 mapping for the
 * case where D3/#24 picked the unconditional AU0001 read over an AU0008/AU0004
 * candidate — decided alongside #25/#27, 2026-07-28). No production/meta or
 * updater/execution layer applies here (those assume an AU0008/AU0004
 * winner); FirmwareDeliveryAPIv1 enrichment is skipped entirely for this case
 * (D2/#23).
 */
export interface OfflineWinnerInfo {
  /** `latest.operation_ts` — when the offline-terminal update actually happened. */
  readonly operationTs: Unknownable<string>;
  /** `source_intake.file_name` — which uploaded offline-log file this came from. */
  readonly sourceFileName: Unknownable<string>;
  /**
   * `source_intake.created_by` — who transferred/uploaded that offline-log
   * file (NOT `latest.attribution_ts`'s updater; this is the file-intake
   * actor). Provisional local display addition, pending an ADR-004 amendment
   * to formally bring `source_intake` beyond `file_name` into scope.
   */
  readonly transferredBy: Unknownable<string>;
  /**
   * `source_intake.added_time` — when that offline-log file was transferred/
   * uploaded (distinct from `operationTs`, the recorded update time itself).
   * Provisional local display addition, pending the same ADR-004 amendment.
   */
  readonly transferTime: Unknownable<string>;
}

/**
 * One row of AU0001's `attribution_list` — the device's fuller offline
 * attribution history, shown as advisory context beneath the winner's own
 * `OfflineWinnerInfo` fields to give the operator the most consistent picture
 * of offline activity for this device. NOT part of ADR-004 D3 selection (only
 * `latest` competes there) and not proof any entry is the terminal one.
 *
 * Provisional local display addition: `attribution_list` / correction-chain
 * bookkeeping was previously out of scope per the D6 amendment
 * (au0001-response-mapper.ts); surfacing it here is a local, pre-ADR display
 * change pending a formal ADR-004 amendment.
 */
export interface OfflineAttributionEntry {
  readonly executionStatus: Unknownable<string>;
  readonly operationTs: Unknownable<string>;
}

/**
 * One row of the ADR-004 D6 offline augment list (redefined by #35; #27):
 * FirmwareDeliveryAPIv1's `ListFirmwarePackageMeta` records for this device
 * (updateMethod = "offline update"), shown below an `OfflineWinnerInfo` —
 * advisory, self-reported, not proof of execution, NOT part of D3 selection.
 *
 * `ListFirmwarePackageMeta` returns a richer record (method, release,
 * draft_solution, device_model, auto_programming, software_uid,
 * operation_time, operated_by, firmware_package_ref); only the three fields
 * meaningful as a compact advisory row are surfaced here (decided
 * 2026-07-29, alongside the capability's real signature) — the rest are
 * intentionally not carried into the display contract, not silently lost
 * (they never leave firmware-delivery-offline-bundles-mapper.ts).
 */
export interface OfflineBundleRecord {
  readonly release: Unknownable<string>;
  readonly operationTime: Unknownable<string>;
  readonly operatedBy: Unknownable<string>;
}
